import {
  $findTableNode,
  $isTableCellNode,
  TableCellNode,
  TableExtension,
  TableNode,
} from "@lexical/table";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isDecoratorNode,
  $isElementNode,
  $isParagraphNode,
  $isRangeSelection,
  $isRootNode,
  $isTextNode,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  configExtension,
  defineExtension,
  KEY_ARROW_UP_COMMAND,
  safeCast,
  type LexicalEditor,
  type LexicalExtension,
  type LexicalNode,
  type ParagraphNode,
} from "lexical";
import { DEFAULT_LIMITS, type DocumentLimits } from "../../core/limits";
import { BlockIdExtension, registerBlockIdentity } from "../block-id/extension";
import { $rootBlockOf } from "../block-id/operations";
import { $editTable, $insertTable, $tableState, type TableState } from "./adapter";
import { EDIT_TABLE_COMMAND, INSERT_TABLE_COMMAND } from "./commands";

export interface TablesConfig {
  /** `maxTableRows` and `maxTableColumns` bound what inserting a row or column may grow a table to. */
  limits: DocumentLimits;
}

export interface TablesOutput {
  subscribe(listener: () => void): () => void;
  /** The table the selection is in, or `null`. The same object until something about it changes. */
  getState(): TableState | null;
  /** For `register` only. */
  internal: { publish(state: TableState | null): void };
}

const sameState = (a: TableState | null, b: TableState | null): boolean => {
  if (a === null || b === null) return a === b;
  return (Object.keys(a) as (keyof TableState)[]).every((key) => a[key] === b[key]);
};

/**
 * The lines of inline content a block amounts to: one for a block of text, one per item or per
 * cell for anything that holds blocks. Nodes are moved, not copied, so a caret inside them stays.
 */
function $linesOf(node: LexicalNode): LexicalNode[][] {
  if (!$isElementNode(node)) return $isDecoratorNode(node) ? [] : [[node]];
  const children = node.getChildren();
  const holdsBlocks = children.some((child) => $isElementNode(child) && !child.isInline());
  return holdsBlocks ? children.flatMap($linesOf) : [children];
}

/** Highlighted code is plain text once it is outside a code block. */
const $asInline = (node: LexicalNode): LexicalNode =>
  $isTextNode(node) && node.getType() === "code-highlight"
    ? $createTextNode(node.getTextContent())
    : node;

/**
 * A cell holds paragraphs and nothing else (no lists, headings, code, or tables), which is what
 * the document format says a stored table is. Most ways of making another block in a cell are
 * refused before they start; this is what makes the rule hold whatever gets past them, so that a
 * document the editor saves is always one it can open. Text is never dropped.
 */
function $keepCellToParagraphs(cell: TableCellNode): void {
  for (const child of cell.getChildren()) {
    if ($isParagraphNode(child)) continue;
    const paragraphs: ParagraphNode[] = $linesOf(child).map((line) =>
      $createParagraphNode().append(...line.map($asInline)),
    );
    for (const paragraph of paragraphs) child.insertBefore(paragraph);
    child.remove();
  }
}

/**
 * A table is a root-level block. One that lands inside a list item or a quote (a paste two lists
 * deep) moves out to directly after the block it landed in. Inside a cell it is left to the cell's
 * transform, which turns it into text.
 */
function $keepTableAtRoot(table: TableNode): void {
  const parent = table.getParent();
  if (parent === null || $isRootNode(parent) || parent.getType() === "tablecell") return;
  $rootBlockOf(table)?.insertAfter(table);
}

/** Whether the caret is on the first line of `block`, as laid out: a long cell wraps. */
function isOnFirstLine(editor: LexicalEditor, blockKey: string): boolean {
  const element = editor.getElementByKey(blockKey);
  const selection = element?.ownerDocument.getSelection() ?? null;
  if (element === null || selection === null || selection.rangeCount === 0) return true;
  const range = selection.getRangeAt(0);
  const caret = range.getClientRects()[0] ?? range.getBoundingClientRect();
  // An empty line has no rectangle to measure, and only one line to be on.
  if (caret.height === 0) return true;
  return caret.top - element.getBoundingClientRect().top < caret.height * 0.75;
}

/**
 * Upstream makes a paragraph to land in when the caret leaves a table that ends the document. It
 * does nothing for one that starts it, where ArrowUp goes nowhere and there is no way to write
 * above the table. Up from the first line of the first row of such a table makes that paragraph.
 */
function $escapeAbove(editor: LexicalEditor, event: KeyboardEvent): boolean {
  if (event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) return false;
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
  const anchor = selection.anchor.getNode();
  const cell = $findMatchingParent(anchor, $isTableCellNode);
  const table = cell === null ? null : $findTableNode(cell);
  if (cell === null || table === null) return false;
  if (!$isRootNode(table.getParent()) || table.getPreviousSibling() !== null) return false;
  if (table.getFirstChild()?.is(cell.getParent()) !== true) return false;
  const first = cell.getFirstChild();
  if (first === null || !(first.is(anchor) || first.isParentOf(anchor))) return false;
  if (!isOnFirstLine(editor, first.getKey())) return false;
  event.preventDefault();
  const paragraph = $createParagraphNode();
  table.insertBefore(paragraph);
  paragraph.select();
  return true;
}

/**
 * Tables: upstream's nodes, cell selection, and keyboard handling, configured for what this editor
 * supports (no merged cells, no nested tables, no cell colors), plus the table's block ID and the
 * two commands the rest of the editor reaches it through. Loaded on demand: see
 * `src/react/tables.ts`.
 */
export const TablesExtension: LexicalExtension<
  TablesConfig,
  "z-editor/tables",
  TablesOutput,
  unknown
> = defineExtension({
  name: "z-editor/tables",
  config: safeCast<TablesConfig>({ limits: DEFAULT_LIMITS }),
  dependencies: [
    BlockIdExtension,
    configExtension(TableExtension, {
      // Upstream splits merged cells and removes cell colors with node transforms of its own.
      hasCellMerge: false,
      hasCellBackgroundColor: false,
      hasNestedTables: false,
      hasTabHandler: true,
      hasHorizontalScroll: true,
    }),
  ],
  build(): TablesOutput {
    let state: TableState | null = null;
    const listeners = new Set<() => void>();
    return {
      subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
      getState: () => state,
      internal: {
        publish(next) {
          if (sameState(state, next)) return;
          state = next;
          for (const listener of listeners) listener();
        },
      },
    };
  },
  register(editor, config, state) {
    const { publish } = state.getOutput().internal;
    return mergeRegister(
      registerBlockIdentity(editor, TableNode),
      editor.registerNodeTransform(TableCellNode, $keepCellToParagraphs),
      editor.registerNodeTransform(TableNode, $keepTableAtRoot),
      editor.registerCommand(
        KEY_ARROW_UP_COMMAND,
        (event) => editor.isEditable() && $escapeAbove(editor, event),
        // The priority upstream's arrow handling and the suggestion menu both use, where order of
        // registration decides: after the menu (an earlier dependency of the editor), which keeps
        // its arrows while it is open, and before upstream's, registered per table as they appear.
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        INSERT_TABLE_COMMAND,
        (payload) => {
          const result = $insertTable(payload, config.limits);
          payload.outcome.refused = result.ok ? null : result.reason;
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        EDIT_TABLE_COMMAND,
        (payload) => {
          const result = $editTable(payload.edit, config.limits);
          payload.outcome.refused = result.ok ? null : result.reason;
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      // Reads only; the one write to the editor from here is a command somebody dispatched.
      editor.registerUpdateListener(({ editorState }) => {
        publish(editorState.read(() => $tableState(config.limits)));
      }),
      () => publish(null),
    );
  },
});
